<template>
  <article v-if="content" class="the-footer-link-collection flex items-center">
    <div class="content flex gap-200 w-full items-center flex-wrap">
      <div v-if="type === 'app'" class="text">
        <span class="headline-h5-headline">
          {{ content.headline }}
        </span>

        <p class="mt-100 caption-regular text-secondary-secondary">
          {{ content.caption }}
        </p>
      </div>

      <ul
        v-if="links.length"
        class="list flex flex-wrap"
        :class="[
          type === 'app' ? 'gap-200' : 'gap-750',
          {
            'justify-start': type === 'app',
            'social-list': type === 'social',
          },
        ]"
      >
        <li v-for="{ title, to, ...imgProps } in links" :key="title">
          <HyperLink v-bind="{ title, to }">
            <LcImage :class="{ 'app-image': type === 'app' }" v-bind="{ ...imgProps }" />
          </HyperLink>
        </li>
      </ul>
    </div>
  </article>
</template>

<script lang="ts" setup>
import type { AppDownloadLink } from '#content/shared/@types/generated/cms/graphql-schema-types'

type LinkCollectionLink = {
  alt: string
  height: number
  src: string
  title: string
  to: string
  width: number
}

type TheFooterLinkCollectionProps = {
  fullWidth?: boolean
  gap: '2xl' | 'md'
  type: 'app' | 'social'
}

const props = defineProps<TheFooterLinkCollectionProps>()

const { getFooterImage } = useAsset()
const { t } = useI18n()

const { data: footer } = useContent().getFooter()

const content = computed(() => {
  if (!footer.value?.items?.[0]?.socialMedia) return null
  const contentType = props.type === 'app' ? 'appDownload' : 'socialMedia'
  const content = footer.value?.items?.[0][contentType]

  // TODO Remove fallbacks when contentful schema finally gets it’s shit together
  return {
    caption: content?.caption ?? null,
    headline: content?.headline ?? '',
    linksCollection: content?.linksCollection ?? null,
  }
})

const links = computed<LinkCollectionLink[]>(() => {
  if (!content.value) return []
  const { linksCollection } = content.value

  // TODO Remove fallbacks when contentful schema finally gets it’s shit together
  return (
    linksCollection?.items.filter(isTruthy).map((item) => {
      let icon = undefined

      if (props.type === 'app') {
        icon = (item as AppDownloadLink).image
      }

      if (props.type === 'social') {
        icon = getFooterImage(`${props.type}-${normalizeKey(item.type ?? '')}`)
      }

      return {
        alt: item.type ?? '',
        height: icon?.height ?? 0,
        src: icon?.url ?? '',
        title: t(`thefooter.${props.type}.link.title`, { platform: item.type }),
        to: item.link ?? '',
        width: icon?.width ?? 0,
      }
    }) ?? []
  )
})
</script>

<style lang="scss" scoped>
.the-footer-link-collection {
  .content {
    column-gap: token('size/space/gap/600');
  }

  .list {
    a {
      position: relative;
      display: block;

      @include focus-state {
        &::before {
          inset: -#{token('size/border/200')};
          border-radius: token('size/radius/s');
          box-shadow: 0 0 0 token('size/border/200') token('color/border/accent/focused/focused');
        }
      }
    }
  }

  .social-list {
    flex-grow: 1;
    justify-content: space-between;
    max-width: 26.5rem;
  }

  .app-image:deep(img) {
    width: auto;
    height: 2.5rem;
  }
}
</style>
